/**
 * =============================================================================
 * ADMIN UI - ENTRY POINT
 * =============================================================================
 *
 * Thin mount for the BigCommerce Admin Dashboard (ADR-025 B-1 /
 * BL-ADMIN-WORKBENCH-IA-002): providers + `AdminApp`, nothing else. All
 * session gating, release-flag surface selection (§3.5 — `AdminShell` vs the
 * legacy flat layout), and navigation live under `components/Shell/`.
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 *
 * CHANGE LOG:
 * - Phase 0B: Wired up SessionProvider and QuestionQueue (previously placeholder)
 * - Phase 0C: Added AnalyticsDashboard for metrics visualization (Task 0C-36)
 * - Phase 1: Added AdminErrorBoundary to catch and handle React errors (P1-18)
 * - Phase 1: Added token expiration warning banner (P1-14)
 * - Phase 1: Added ToastProvider for admin action notifications (P1-7)
 * - ADR-025 A-H: Self-hosted Source Sans 3 via @fontsource (no runtime injectFont)
 * - ADR-025 B-1: `AdminContent` replaced by the Shell composition — the flat
 *   panel stack moved verbatim to `Shell/LegacyAdminLayout` (the flag-OFF
 *   fallback); the new `AdminShell` mounts behind the tenant
 *   `adminWorkbenchShell` release flag (M17, default OFF)
 *
 * =============================================================================
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { GlobalStyles } from '@bigcommerce/big-design';
import { SessionProvider } from './providers/SessionProvider';
import { ToastProvider } from './providers/ToastProvider';
import { AdminErrorBoundary } from './components/ErrorBoundary';
import { AdminApp } from './components/Shell';
// ADR-025 A-H (BL-ADMIN-WORKBENCH-IA-001G): Source Sans 3 — the standard
// BigCommerce admin font — is SELF-HOSTED via @fontsource (bundled by Vite,
// served same-origin). The previous runtime `injectFont()` appended a
// fonts.googleapis.com stylesheet from inside the CSP-constrained admin
// iframe: an external runtime dependency and a FOUT risk, both removed.
// Weights match the ones the removed Google request loaded (200/300/400/600).
import '@fontsource/source-sans-3/200.css';
import '@fontsource/source-sans-3/300.css';
import '@fontsource/source-sans-3/400.css';
import '@fontsource/source-sans-3/600.css';

/**
 * Root App component.
 *
 * Wraps the application in:
 * 1. GlobalStyles - BigDesign's CSS reset and base styles
 * 2. AdminErrorBoundary - top-level React error containment
 * 3. ToastProvider - admin action notifications
 * 4. SessionProvider - Extracts and manages BigCommerce JWT from URL
 *
 * Then thin-mounts `AdminApp` (session gate → release-flag surface selection).
 */
function App(): React.ReactElement {
  return (
    <>
      <GlobalStyles />
      <AdminErrorBoundary>
        <ToastProvider>
          <SessionProvider>
            <AdminApp />
          </SessionProvider>
        </ToastProvider>
      </AdminErrorBoundary>
    </>
  );
}

// Mount the application
const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

export default App;
